import { FC } from "preact/compat";
import { useLayoutEffect, useRef } from "preact/hooks";
import GuideExample from "./GuideExample";
import cn from "classnames";

const SUPPORTED_MD = [
    {
        name: "Headers (1-6)",
        example:
            "# Largest header\n## Second largest header\n###### Smallest header",
        description:
            "Headers are created by prefixing the line with 1 to 6 '#' characters.",
    },
    {
        name: "Unordered list",
        example: "- First item\n- Second item\n- Third item",
        description:
            "Unordered lists are created by prefixing the line with a '-' character.",
    },
    {
        name: "Ordered list",
        example: "1. First item\n2. Second item\n3. Third item",
        description:
            "Ordered lists are created by prefixing the line with a number followed by a '.' character.",
    },
    {
        name: "Links (tooltips)",
        example:
            "[Open profile](profile)\n\n[Open tracker](header.tracker)\n\n[Custom activities|Click to configure custom activities](profile.custom-activities.header)\n\n[No link|but with tooltip]()",
        description:
            "Links can be used to open other tags, or navigate to a different page, or add a tooltip. It's a shortcut for the tooltip template. Admins don't need to open sidebar with a command or navigate to a different page if target tag is on different page, everything is done automatically.",
    },
];

const SUPPORTED_TEMPLATES = [
    {
        label: "Day status box",
        name: "day-box",
        description: "Depicts a day box from a month view in the calendar.",
        params: [
            {
                name: "color",
                description:
                    "Color of a box. Possible values: 'green', 'yellow', 'red', 'gray', 'striped'.",
            },
            {
                name: "content",
                description: "Text inside the box.",
            },
        ],
        example:
            "Green button\n{{day-box|color=green|content=5}}\nRed button\n{{day-box|color=red|content=12}}\n'Striped' button\n{{day-box|color=striped|content=25}}",
    },
    {
        label: "Activity box",
        name: "activity-box",
        description: "A box that represents an activity in the calendar.",
        params: [
            {
                name: "type",
                description:
                    "Type of an activity. Possible values: 'calendar', 'planning', 'task', 'timer', 'manual'.",
            },
            {
                name: "title",
                description: "Title to be shown.",
            },
            {
                name: "start",
                description:
                    "Start time to be shown. For instance, '12:00'. Can be omitted.",
            },
        ],
        example:
            "Todays activities:\n{{activity-box|type=calendar|title=Lunch meeting|start=09:00}}\n{{activity-box|type=timer|title=Coffee break|start=10:00}}\n{{activity-box|type=manual|title=Work on project|start=12:00}}",
    },
    {
        label: "Button",
        name: "button",
        description: "A button that can perform an action when clicked.",
        params: [
            {
                name: "action",
                description:
                    "Action to be performed when the button is clicked.",
                descriptionLong: `Action is a set of instructions divided by ';' character.
Instruction is either simple (without any parameters, just a name of an action) or complex (with parameters).
If action has parameters, they are passed as key-value pairs separated by '=' character. E.g 'open_discovery(tag=admin-time.month.contracts)'.

Available actions:
- goto(page=page) - navigates to a given page (e.g. 'profile' or 'admin/time')
- open_discovery(tag=tag) - opens a discovery item with a given tag
- admin_time_open_sidebar - opens a sidebar in the month view in the calendar`,
            },
            {
                name: "content",
                description: "Text inside the button.",
            },
        ],
        example:
            "This button opens a different discovery item when clicked\n{{button|action=open_discovery(tag=admin-time.month.contracts)|content=Open contracts discovery}}\n",
    },
    {
        label: "Styled text",
        name: "styled-text",
        description: "Regular text with styles applied to it.",
        params: [
            {
                name: "font-weight",
                description: "Weight of the text. Can be omitted.",
                descriptionLong:
                    "Numeric value between 1 and 1000, both values included. Higher numbers represent weights that are bolder than (or as bold as) lower numbers. Numbers are usually multiples of a hundred (e.g. 300, 400, 500, 600 ...) where 400 is normal, 700 is bold and 100-300 thin.\nSee standard value mapping: https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight#common_weight_name_mapping.",
            },
            {
                name: "font-style",
                description:
                    "Style of the font (normal or italic). Can be omitted.",
                descriptionLong:
                    "Property sets whether a font should be styled with a normal or italic.\n Can be 'italic' or 'normal' when 'normal' is default value.",
            },
            {
                name: "text-color",
                description: "Color of the text. Can be omitted.",
                descriptionLong:
                    "Can be any hex color value (e.g #009900), rgb (e.g rgb(34, 12, 64, 0.6)), or predefined color name (e.g blue, orange, pink ...)\n All predefined color names can be found here: https://developer.mozilla.org/en-US/docs/Web/CSS/named-color.\n More ways to set the color can be found here: https://developer.mozilla.org/en-US/docs/Web/CSS/color.",
            },
            {
                name: "content",
                description: "Actual text to apply styles to.",
            },
        ],
        example:
            "{{styled-text|font-weight=700|content=Im bold}}\n\n{{styled-text|font-weight=200|font-style=italic|content=Im light and italic}}\n\n{{styled-text|text-color=#0000ff|content=My color set with hex}}\n\n{{styled-text|text-color=coral|content=My color set with color name}}",
    },
    {
        label: "Icon",
        name: "icon",
        description: "Icon from predefined list of icons.",
        params: [
            {
                name: "name",
                description: "Name of the icon to show.",
                descriptionLong:
                    "Names are case-insensitive, so they can be written, for example, with a lowercase letter. Spaces can also be changed to '-' (dash) (e.g. 'question-mark').\nThe source of the images can be found here: https://fonts.google.com/icons. All names are taken from here.\nAvailable names: 'Question Mark', 'Person', 'Home', 'Chevron Right', 'Chevron Left', 'Timer', 'Back Hand', 'More Vert', 'Search', 'View Timeline', 'Assignment', 'Calendar Month', 'Link', 'Settings', 'Magic', 'Warning'.",
            },
            {
                name: "size",
                description: "Size of the icon.",
                descriptionLong:
                    "By default the size of the icon is 'md' (medium) but it is possible to set it to 'lg' (large).",
            },
        ],
        example:
            "{{icon|name=Question Mark}} {{icon|name=Person}} {{icon|name=Home}} {{icon|name=Chevron Right}} {{icon|name=Chevron Left}} {{icon|name=Timer}} {{icon|name=Back Hand}} {{icon|name=More Vert}} {{icon|name=Search}} {{icon|name=View Timeline}} {{icon|name=Assignment}} {{icon|name=Calendar Month}} {{icon|name=Link}} {{icon|name=Settings}} {{icon|name=Magic}} {{icon|name=Warning}}\n\nLarge:\n{{icon|name=back-hand|size=lg}}",
    },
    {
        label: "Tooltip (deprecated)",
        name: "tooltip",
        description:
            "Use markdown links instead. Text that opens tooltip on hover.",
        params: [
            {
                name: "action",
                description:
                    "Action to be performed when the content is clicked.",
                descriptionLong:
                    "Logic is exactly the same as for button action (see button template).\n\nAvailable actions:\n- goto(page=page) - navigates to a given page (e.g. 'profile' or 'admin/time')",
            },
            {
                name: "tooltip-content",
                description: "Text inside tooltip.",
            },
            {
                name: "content",
                description: "Text to hover on.",
            },
        ],
        example:
            "{{tooltip|tooltip-content=I am the content of the tooltip|content=Hover on me}}\n\n{{tooltip|action=goto(page=profile)|content=I'm a link}}\n\n{{tooltip|action=goto(page=profile)|tooltip-content=Click to navigate|content=Hover on me}}",
    },
];

const EXAMPLE = `\
## Example

In your {{styled-text|text-color=#55828B|content=time-reporting}} you can see that {{styled-text|font-weight=700|content=each day}} has a status box with a number of a day.
This status box can have different {{tooltip|tooltip-content=Description|content=colors}} depending on the status of the day:
- {{day-box|color=yellow|content=7}} - day has been stopped
- {{day-box|color=striped|content=7}} - day is not finished and you have reported all the required contacted hours but you still have some unreported activities
- {{day-box|color=red|content=7}} - day is in the past and you have not reported the required contracted hours
- {{day-box|color=green|content=7}} - day is in the past, you have reported all the activities or day is finished
- {{day-box|color=gray|content=7}} - today or a future day

{{icon|name=Home}} - You will see that icon when you on the my profile page.

### Links
{{button|action=admin_time_open_sidebar;open_discovery(tag=admin-time.month.day-status-selector)|content=Show me where to mark day as finished or stopped}}

{{button|action=admin_time_open_sidebar;open_discovery(tag=admin-time.month.contracts)|content=Show me where I can see my contracted hours for a day}}
`;

let lastScrollTop = 0;

const Guide: FC = () => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        element.scrollTop = lastScrollTop;

        return () => {
            lastScrollTop = element.scrollTop;
        };
    }, []);

    return (
        <div ref={ref} className="overflow-y-overlay h-[90vh] max-h-[90vh] p-4">
            <h1 className="mb-2 border-b border-gray-200 pb-1 text-2xl font-bold">
                Admin's Guide
            </h1>
            <p>
                Manual's content is a text which can contain:{" "}
                <strong>plain text</strong>,{" "}
                <strong>some markdown elements</strong> and{" "}
                <strong>custom templates</strong>.
            </p>
            <h2 className="my-2 text-lg font-bold">Markdown Elements</h2>
            {SUPPORTED_MD.map((md) => (
                <div key={md.name} className="mb-6">
                    <h3 className="mb-1 font-bold">{md.name}</h3>
                    <p className="mb-1">{md.description}</p>
                    <GuideExample content={md.example} className="mb-2" />
                </div>
            ))}
            <div className="my-4 h-[1px] w-full bg-gray-300" />
            <h2 className="my-2 text-lg font-bold">Custom templates</h2>
            <p>
                Non-standard layout elements can be created using custom
                templates. General structure of a template is:
            </p>
            <pre className="rounded-lg border border-gray-300 bg-gray-100 px-1 py-0.5">
                {"{{templateName|param1=value1|param2=value2}}"}
            </pre>
            <p>
                <strong className="text-sky-600">templateName</strong> - name of
                a template (all the possible names are listed below)
            </p>
            <p>
                <strong className="text-sky-600">param1, param2...</strong> -
                parameters that are passed to the template (each template has
                its own set of parameters, they are listed below)
            </p>
            <h3 className="my-2 text-lg font-bold">Supported templates</h3>
            {SUPPORTED_TEMPLATES.map((template, idx) => (
                <div
                    key={template.name}
                    className={cn(
                        "-mx-4 border-t border-gray-300 p-4",
                        idx % 2 && "bg-gray-50",
                        idx === SUPPORTED_TEMPLATES.length - 1 && "border-b",
                    )}
                >
                    <h4 className="mb-1 text-2xl font-bold">
                        {template.label}
                    </h4>
                    <p className="mb-1">{template.description}</p>
                    <GuideExample content={template.example} className="mb-2" />
                    <h5 className="mt-1 font-bold">Template name</h5>
                    <pre className="font-bold text-sky-500">
                        {template.name}
                    </pre>
                    <h5 className="mt-1 font-bold">Parameters:</h5>
                    <ul className="list-disc pl-4">
                        {template.params.map((param) => (
                            <li key={param.name}>
                                <p>
                                    <strong className="text-sky-500">
                                        {param.name}
                                    </strong>{" "}
                                    - {param.description}
                                </p>
                                {param.descriptionLong && (
                                    <div className="text-sm">
                                        {param.descriptionLong
                                            .split("\n")
                                            .map((paragraph, idx) => (
                                                <p className="mt-1" key={idx}>
                                                    {paragraph}
                                                </p>
                                            ))}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            <div className="my-8">
                <h2 className="mb-2 text-lg font-bold">Playground</h2>
                <p className="mb-2">
                    Here's an example content, you can edit and see how the
                    result looks like.
                </p>
                <GuideExample content={EXAMPLE} className="min-h-[800px]" />
            </div>
        </div>
    );
};

export default Guide;
